import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { getInternalQuotations } from '@/features/operations/api';
import { formatQuotationDate, originLabels } from '@/features/operations/labels';
import { usePriceDisplay } from '@/features/operations/PriceDisplayProvider';
import {
  allQuotationStatuses,
  allQuotationStatusesFilter,
  defaultQuotationStatusFilter,
  getQuotationStatusLabel,
} from '@/features/operations/quotationStatus';
import type { QuotationFilters, QuotationRequestItem } from '@/features/operations/types';
import { StatusChip } from '@/components/StatusChip';

// Refetch delay for free-text search, so each keystroke doesn't round-trip to the API.
const SEARCH_DEBOUNCE_MS = 400;

/**
 * Where PhoneTec finds a request: search, filter, open it.
 *
 * The filters live in the URL rather than in state, so coming back from a request's detail
 * lands on the same search instead of an empty list. With no status in the URL the list shows
 * the Quoted requests, the ones still being worked on (BR-017).
 */
export function QuotationsPage() {
  const { formatMoney } = usePriceDisplay();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search') || '';
  const statusFilter = searchParams.get('status') || defaultQuotationStatusFilter;
  const dateFrom = searchParams.get('dateFrom') || '';
  const dateTo = searchParams.get('dateTo') || '';
  const [searchInput, setSearchInput] = useState(search);
  const [quotations, setQuotations] = useState<QuotationRequestItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  /// Replaces the history entry instead of adding one: going back should leave the list, not
  /// undo a filter one keystroke at a time.
  const setFilter = (key: string, value: string) =>
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);

        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }

        return next;
      },
      { replace: true },
    );

  // Debounces the free-text search box; status and date filters apply immediately since they
  // are discrete choices instead of keystrokes.
  useEffect(() => {
    const timeout = setTimeout(() => {
      const trimmed = searchInput.trim();

      if (trimmed !== search) {
        setFilter('search', trimmed);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
    // `search` and `setFilter` are left out on purpose: this reacts to typing only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  useEffect(() => {
    const filters: QuotationFilters = {
      status: statusFilter === allQuotationStatusesFilter ? undefined : statusFilter,
      search: search || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    };

    const loadQuotations = async () => {
      if (!token) {
        return;
      }

      setIsLoading(true);
      setErrorMessage(null);

      try {
        setQuotations(await getInternalQuotations(token, filters));
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : 'No pudimos cargar las solicitudes.',
        );
      } finally {
        setIsLoading(false);
      }
    };

    void loadQuotations();
  }, [token, statusFilter, search, dateFrom, dateTo]);

  if (!token) {
    return null;
  }

  return (
    <Stack spacing={4}>
      <PageHeader
        eyebrow="COTIZACIONES"
        title="Solicitudes de cotización"
        description="Buscá una solicitud y abrila para gestionarla y registrar la recepción del equipo."
        badge="Uso interno"
      />

      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

      <SectionCard
        title="Solicitudes"
        description="Las que llegan por la web y las que se registran en el mostrador."
      >
        <Stack spacing={2.5}>
          <Box>
            <Button variant="contained" component={RouterLink} to="/admin/quotations/new">
              Nueva cotización
            </Button>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Buscar"
                placeholder="Marca, modelo o cliente"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <TextField
                fullWidth
                label="Filtrar por estado"
                select
                value={statusFilter}
                onChange={(event) =>
                  // The default stays out of the URL, so a plain link to the list is the default.
                  setFilter(
                    'status',
                    event.target.value === defaultQuotationStatusFilter ? '' : event.target.value,
                  )
                }
              >
                <MenuItem value={allQuotationStatusesFilter}>Todos</MenuItem>
                {allQuotationStatuses.map((option) => (
                  <MenuItem key={option} value={option}>
                    {getQuotationStatusLabel(option)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <TextField
                fullWidth
                label="Desde"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={dateFrom}
                onChange={(event) => setFilter('dateFrom', event.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <TextField
                fullWidth
                label="Hasta"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={dateTo}
                onChange={(event) => setFilter('dateTo', event.target.value)}
              />
            </Grid>
          </Grid>

          {isLoading ? (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 3 }}>
              <CircularProgress color="secondary" size={28} />
            </Box>
          ) : quotations.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No hay solicitudes que coincidan con el filtro elegido.
            </Typography>
          ) : (
            // Only the table scrolls sideways on a narrow screen, never the page.
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell sx={{ minWidth: 180 }}>Contacto</TableCell>
                    <TableCell sx={{ minWidth: 160 }}>Modelo</TableCell>
                    <TableCell align="right">Precio estimado</TableCell>
                    <TableCell>Origen</TableCell>
                    <TableCell>Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {quotations.map((quotation) => {
                    const detailPath = `/admin/quotations/${quotation.id}`;
                    // The search travels along so the way back returns to it.
                    const detailState = { from: searchParams.toString() };

                    return (
                      <TableRow
                        key={quotation.id}
                        hover
                        onClick={() => navigate(detailPath, { state: detailState })}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatQuotationDate(quotation.createdAt)}
                        </TableCell>
                        <TableCell>
                          {/* The row is clickable with a mouse; the link is the way in from
                              the keyboard and for screen readers. */}
                          <Link
                            component={RouterLink}
                            to={detailPath}
                            state={detailState}
                            aria-label={`Abrir solicitud de ${quotation.contact.name}`}
                            underline="hover"
                            color="inherit"
                            fontWeight={600}
                            onClick={(event) => event.stopPropagation()}
                          >
                            {quotation.contact.name}
                          </Link>
                          {quotation.photos.length || quotation.lastContact ? (
                            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                              {quotation.photos.length ? (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  label={`${quotation.photos.length} foto${quotation.photos.length > 1 ? 's' : ''}`}
                                />
                              ) : null}
                              {quotation.lastContact ? (
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                  label={`Contactado por ${
                                    quotation.lastContact.contactedBy?.fullName || 'PhoneTec'
                                  } · ${formatQuotationDate(quotation.lastContact.createdAt)}`}
                                />
                              ) : null}
                            </Stack>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {quotation.brand} {quotation.model}
                          </Typography>
                          {quotation.storageGb === null ? null : (
                            <Typography variant="caption" color="text.secondary">
                              {quotation.storageGb}GB
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {quotation.estimate
                            ? formatMoney(
                                quotation.estimate.amountMinor,
                                quotation.estimate.currency,
                              )
                            : '—'}
                        </TableCell>
                        <TableCell>{originLabels[quotation.origin] || quotation.origin}</TableCell>
                        <TableCell>
                          <StatusChip domain="quotation" status={quotation.status} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </Stack>
      </SectionCard>
    </Stack>
  );
}
